import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import { useIntl } from '@jetshop/intl';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import loadable from '@loadable/component';
import { styled } from 'linaria/react';
import React, { useContext } from 'react';
import MaxWidth from '../Layout/MaxWidth';
import { Favourite } from '../ProductList/Favourite';
import Spinner from '../ui/Spinner';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductPageLoadingState from './ProductPageLoadingState';
import { theme } from '../Theme';
import { Breadcrumbs } from '../../r3-lib/components/ui/Breadcrumbs/Breadcrumbs';
import { ProductRow, ProductRowItem } from '../ContentComponents/ProductRow';
import DynamicCategoryRenderer from '../../r3-lib/components/layout/DynamicCategoryRenderer/DynamicCategoryRenderer';
import { uspComponents } from '../ContentComponents/ContentComponents';
import ChannelContext from '@jetshop/core/components/ChannelContext';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />
  }
);

const ProductContainer = styled(MaxWidth)`
  width: 100%;
  max-width: 78rem;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 10px 0;
  /* margin-bottom: 20px; */
  ${theme.below.lg} {
    display: block;
    padding: 0;
  }
`;

const InnerWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  margin: auto;
  width: 100%;
  margin-bottom: 60px;
  padding: 0 2rem;

  .product-price {
    font-family: ${theme.fonts.primary};
    font-weight: 500;
    font-size: 24px;
    line-height: 33px;
    margin: 20px 0;

    > div {
      display: flex;
      margin-left: -10px;
    }

    .price,
    .new-price,
    .old-price {
      margin-left: 10px;
    }

    .new-price {
      color: ${theme.colors.red};
    }
    .old-price {
      color: ${theme.colors.darkGray};
    }
  }

  ${theme.below.lg} {
    flex-direction: column;
    max-width: 100%;
    padding: 0;
  }
`;

const BottomWrapper = styled('div')`
  width: 100%;
  max-width: 78rem;
  padding: 0 2rem;

  h2 {
    font-family: ${theme.fonts.primary};
    font-weight: 700;
    font-size: 25px;
    line-height: 34px;
    text-align: center !important;
    letter-spacing: 0.04em;
    margin-bottom: 20px;
  }

  ${theme.below.lg} {
    padding: 0;

    h2 {
      text-align: start;
      font-size: 18px;
      line-height: 22px;
      padding: 0 20px;
    }
    .swiper {
      margin: 0;
      margin-left: -7px;
    }
  }
`;

const ImageColumn = styled('div')`
  position: relative;
  max-width: 52%;
  width: 100%;

  [data-flight-image-container] {
    background: #f8f8f8;
  }
  [data-flight-image] {
    /* mix-blend-mode: darken; */
    mix-blend-mode: multiply;
    img {
      mix-blend-mode: multiply;
    }
  }

  .fav-wrapper {
    position: absolute;
    z-index: 1;
    top: 20px;
    right: 25px;

    svg {
      color: ${theme.colors.primary};
      width: 20px !important;
      height: 20px !important;
    }
  }

  ${theme.below.lg} {
    max-width: 100%;
    margin-bottom: 24px;

    .fav-wrapper {
      top: 20px;
      right: 40px;
    }
  }
`;

const ProductInfoColumn = styled('div')`
  display: flex;
  flex-direction: column;
  max-width: 48%;
  width: 100%;
  position: sticky;
  top: 60px;
  padding: 0 90px;

  .inner {
    max-width: 322px;
  }

  .product-titles {
    margin: 10px 0;

    h4 {
      font-family: ${theme.fonts.primary};
      color: ${theme.colors.darkGray};
      font-weight: 500;
      text-transform: uppercase;
    }
  }

  .button-wrapper {
    button {
      font-size: 15px;
    }
  }
  //MOBILE
  ${theme.below.lg} {
    flex: none;
    padding: 0 20px;
    max-width: 100%;

    .inner {
      max-width: 100%;
      .product-titles {
        h4 {
          font-size: 14px;
        }
      }
    }
  }
`;

const ProductTitle = styled('h1')`
  font-family: ${theme.fonts.primary};
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  letter-spacing: 0.04em;
  color: ${theme.colors.blackish};

  ${theme.below.lg} {
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 26px;
  }
`;

const UspsWrapper = styled('div')`
  width: 100%;
  font-family: ${theme.fonts.primary};
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;

  text-align: center;

  .delivery-list {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;

    li {
      margin-left: 10px;
    }

    svg {
      vertical-align: text-top;
      margin-right: 4px;
    }
  }

  .usps-wrapper {
    font-size: 12px;
  }
`;

const ShortDescription = styled('div')`
  p {
    font-family: ${theme.fonts.primary};
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: ${theme.colors.blackish};
  }
`;

const Product = ({ result: { data, loading }, product }) => {
  const initialVariant = useVariantFromUrl();
  const t = useIntl();

  const price = useDynamicPrice(product, selectedVariation);

  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: true
  });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  const { selectedChannel } = useContext(ChannelContext);

  const findPDPUspId = () => {
    switch (selectedChannel?.id) {
      //Cykel&Fritid
      case 1:
        return 839;
      //OnlineCykel
      case 2:
        return 849;
      //XLCykel
      case 3:
        return 850;
      //Sandnas
      case 4:
        return 851;
      //default
      default:
        return 839;
    }
  };

  const pdpUspId = findPDPUspId();

  // Return early if there is no product
  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const images = product.images ?? [];
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const relatedProducts = product.relatedProducts;

  const groups = [
    {
      title: 'Allmänt',
      items: [
        { name: 'Cykeltyp', tip: '' },
        { name: 'Varumärke', tip: '' },
        { name: 'Modellserie', tip: '' },
        { name: 'Årsmodell', tip: '' },
        { name: 'Artikelnummer', tip: '' },
        { name: 'Färg', tip: '' },
        { name: 'Antal växlar', tip: '' },
        { name: 'Vikt(kg)', tip: '' },
        { name: 'Tillverkarens artikelnummer', tip: '' },
        { name: 'Hemsida tillverkare', tip: '' },
        { name: 'Elsystem', tip: '' },
        { name: 'Dammodell', tip: '' },
        {
          name: 'MIPS',
          tip:
            'MIPS gör så att hjälmen rör sig vid sneda slag och att huvudet inte rör sig med hjälmen på samma sätt, vilket leder till minskade krafter som överförs till hjärnan. Detta gör att en MIPS cykelhjälm är ett betydligt bättre skydd, än vad en traditionell cykelhjälm är. MIPS (Multi-directional Impact Protection System) har utvecklats av forskare och hjärnkirurger för att minska de rotationskrafter på hjärnan som följer av vinklade krafter mot huvudet.'
        }
      ]
    },
    {
      title: 'Ram',
      items: [
        { name: 'Ram', tip: '' },
        { name: 'Ramstorlek', tip: '' },
        {
          name: 'Rammaterial',
          tip:
            '<div><h2>Rammaterial</h2><p>Cykelns ram kan vara tillverkad i olika typer av material med olika egenskaper. Vilket material du bör välja styrs av din budget och användingsområdet för cykeln.</p><br/><br/><h3>Aluminium</h3><p><strong>Egenskaper:</strong> Hållbart, relativt lätt, rostbeständigt och budgetvänligt. Aluminium är mer styvt än kolfiber vilket också innebär att du kommer känna av varje stöt mer. </br><strong>Passar till:</strong> Aluminium är för dig som värdesätter både låg vikt och lågt pris. Passar utmärkt för pendlaren som åker långa sträckor på ett relativt platt underlag.<br/><br/><h3>Kolfiber</h3><strong>Egenskaper:</strong> Starkt, rostbeständigt och extremt lätt.<br/><strong>Passar till:</strong> Kolfiber är för dig som prioriterar låg vikt över pris. Kolfiber är det föredragna materialet för professionella cyklister.<br/><br/><h3>Stål</h3><strong>Egenskaper: </strong>Starkt, hållbart och säkert<br/><strong>Passar till: </strong>Om du tenderar att ramla eller köra in i saker är en stålram perfekt i och med att den är så hållbar. Därför kan stål vara en bra alternativ för barn. Eftersom stål väger mer än de andra materialen är det kanske inte det som fitness-cyklisten väljer. </p></div>'
        },
        { name: 'Framgaffel', tip: '' },
        { name: 'Bakgaffel', tip: '' },
        { name: 'Slaglängd fram', tip: '' },
        {
          name: 'Låsbar framgaffel?',
          tip:
            'Med en låsbar gaffel kan du enkelt låsa fast så gaffeln blir stel. Det passar bra om du t.ex cyklar inne i stan där det inte är lika mycket hinder som kräver en dämpad cykel.'
        },
        { name: 'Slaglängd bak', tip: '' },
        {
          name: 'Framgaffeltyp',
          tip:
            '<div><h2>Dämpad gaffel</h2><h3>Luftfjädring</h3><p>Den vanligaste typen av dämpare är luftfjädring, Du kan enkelt justera hur hårt eller mjukt du vill ha din gaffel med hjälp av en dämparpump. Om du har mer luft i blir gaffeln hårdare och mindre luft blir den mjukare.</p><br/><h3>Spiralfjäder</h3><p>Den andra sorten är en spiralfjäder, då är det en fjäder som sköter dämpningen på cykeln. Du kan inte justera lika mycket som du kan med cykel med luft i, däremot kan du på vissa gafflar byta ut till olika sorters fjädringar för att kunna få en hårdare eller mjukare dämpning.</p><br/><h3>Stel gaffel</h3><p>Det är en cykel som inte har någon dämpning alls i gaffeln och klassas då som en stel gaffel som passar mer till stadscykling.</p></div>'
        }
      ]
    },
    {
      title: 'Hjul',
      items: [
        {
          name: 'Hjulstorlek',
          tip:
            '<div><h3>Mountainbike</h3><p>Tänk på att i vissa modeller varierar på hjulstorleken beroende på vilken ramstorlek du väljer. T.ex en Small kan ha 27.5"" medans en Medium kan ha 29"".</p><br/><h3>Standard</h3><p>Vanliga herr- och damcyklar är oftast 28""  och passar de flesta som är över 150cm långa. Är du kortare och inte skulle nå ner kan du gå ner en hjulstorlek till 26"" eller kika på 28"" med 47cm ram. Vi skriver alltid rek. längd i beskrivningen på de olika hjul- och ramstorlekar.  "</p></div>'
        },
        { name: 'Nav fram', tip: '' },
        { name: 'Nav bak', tip: '' },
        { name: 'Fälgar', tip: '' },
        { name: 'Däckdimension', tip: '' },
        { name: 'Framdäck', tip: '' },
        { name: 'Bakdäck', tip: '' },
        { name: 'Hjul', tip: '' }
      ]
    },
    {
      title: 'Drivlina',
      items: [
        { name: 'Vevparti', tip: '' },
        { name: 'Vevlager', tip: '' },
        { name: 'Kedja', tip: '' },
        { name: 'Kassett', tip: '' }
      ]
    },
    {
      title: 'Komponenter',
      items: [
        { name: 'Växelreglage', tip: '' },
        { name: 'Framväxel', tip: '' },
        { name: 'Bakväxel', tip: '' },
        {
          name: 'Bromstyp',
          tip:
            '<div><p>Det finns ett antal olika bromstyper som lämpar som kan lämpa sig bättre eller sämre för olika typer av cykling och olika typer av underlag.</p><br/><h3>V-broms</h3><p>Den stora fördelen med V-broms (klassisk fälgbroms) är dess låga vikt. Denna bromstyp återfinns på många av de vanliga standradcyklarna och man bör tänka på att de kan ha sämre bromseffekt under dåliga väderförhållanden. De har också stort justeringsbehov.</p><br/><h3>Mekanisk skivbroms</h3><p>Skivbromsar är mer vädertåliga än fälgbromar men väger mer. De är också svårare att underhålla själv, men kräver generellt mindre service än fälgbromsar.</p><br/><h3>Hydraulisk skivbroms</h3><p>Hydrauliska och mekaniska skivbromsar fungerar rent funktionsmässigt likadant men det som särskiljer den hydrauliska skivbromsen är att det är olja som används för att trycka bromsklossarna mot skivan, istället för en vajer. Detta innebär att denna bromstyp är mer responsiv samtidigt som bromsklossarna justeras automatiskt tack vare oljetrycket, vilket gör att de nästintill inte har något justeringsbehov alls. Med hydauliska sivbromsar får du också mycket mer känsla i bromsningen.</p><br/><h3>Navbroms</h3><p>Navbroms är den enda bromsen med möjlighet till fotbroms. En nackdel med denna bromstyp är att det kan vara svårt att bromsa när pedalen är i fel läge vilket kan bli problematiskt i kritiska lägen. En fördel är att bromsen är inkapslad vilket innebär ett lågt servicebehov i och med att inget smuts kommer in.</p><br/><h3>Rullbroms</h3><p>Rullbromsar har blivit allt mer ovanliga eftersom dess bromsverkan är sämre men de återfinns ibland på vanliga standardcyklar. En fördel är att de i princip är helt underhållsfria. Det enda man kan behöva göra är att fylla på med bromsfett och eventuellt spänna vajern.</p></div>'
        },
        { name: 'Frambroms', tip: '' },
        { name: 'Bakbroms', tip: '' },
        { name: 'Bromsskiva fram', tip: '' },
        { name: 'Bromsskiva bak', tip: '' },
        { name: 'Handtag', tip: '' },
        { name: 'Styre', tip: '' },
        { name: 'Styrstam', tip: '' },
        { name: 'Styrlager', tip: '' },
        { name: 'Sadelstolpe', tip: '' },
        { name: 'Sadel', tip: '' },
        { name: 'Pedaler', tip: '' }
      ]
    },
    {
      title: 'Motor',
      items: [
        { name: 'Motor', tip: '' },
        {
          name: 'Motorplacering',
          tip:
            '<div><h3>Motorns placeing</h3><p>Elcykelns motor kan ha olika placeringar som innebär olika fördelar och nackdelar.<br/><br/>En motor som är placerad i <strong>framhjulet</strong> gör att alla växeltyper samt fotbroms är möjligt. Nackdelen med detta alternativ är att kablar måste kopplas loss om man vill lossa hjulet. Placeringen innebär också ett något sämre grepp på grund av att mindre tyngd hamnar över framhjulet.<br/><br/>Med motorn i <strong>bakhjulet</strong> får man bra grepp i just bakhjulet. Det är lätt att montera batteriet på en befintlig cykel men problemet med att kablar behöver kopplas loss i samband med att hjulet lossas kvarstår.<br/><br/>En mittmotor som sitter i vevlagret ger bra viktfördelning och bra grepp på både bakhjul och framhjul samt att inga kablar behöver kopplas loss när man lossar hjulen. Nackdelen med detta är att det kan vara svårare att montera ett batteri på en befintlig cykel eftersom en specialanpassad ram kan krävas. Med vissa växelsystem är inte heller fotbroms möjligt. </br></p></div>'
        },
        { name: 'Motoreffekt', tip: '' },
        {
          name: 'Vridmoment',
          tip:
            'Nm är en förkortning på newtonmeter som anger hur kraftig motorn är. T.ex en elcykel med lägre Nm kanske inte orkar lika mycket som en med högre om du ska cykla i uppförsbackar, då är en med högre Nm är lättare att cykla med. De vanligaste vridmomenten kan variera mellan 40-80Nm, flesta elcyklar med enklare motorer har vridmoment på 40Nm.'
        },
        { name: 'Sensorer', tip: '' },
        {
          name: 'Maximal assistanshastighet',
          tip:
            'Maxhastigheten är alltid 25 km/h med elassistans enligt lag, men det går att cykla fortare men då är det utan hjälp. När du kommer upp till 25 km/h kommer motorn kopplas ifrån och du får själv stå för farten tills du kommer under max hastighet igen. '
        },
        { name: 'Motorns ljudnivå', tip: '' },
        { name: 'Maximal räckvidd', tip: '' },
        { name: 'Genomsnittlig räckvidd', tip: '' }
      ]
    },
    {
      title: 'Batteri',
      items: [
        { name: 'Medföljer batteri?', tip: '' },
        { name: 'Batteri', tip: '' },
        { name: 'Batterityp', tip: '' },
        { name: 'Batteriplacering', tip: '' },
        { name: 'Batterikapacitet (Wh)', tip: '' },
        { name: 'Spänning', tip: '' },
        { name: 'Kan laddas på cykeln', tip: '' },
        { name: 'Batteriladdare', tip: 'dsa' },
        { name: 'Laddningstid', tip: '' },
        { name: 'Avtagbart batteri', tip: '' },
        { name: 'Batterikapacitet (Ah)', tip: '' }
      ]
    },
    {
      title: 'Display',
      items: [
        { name: 'Display', tip: '' },
        { name: 'Gåassistans', tip: '' },
        { name: 'Assistansnivåer', tip: '' },
        { name: 'Avtagbar display', tip: '' },
        { name: 'Displayens position', tip: '' }
      ]
    },
    {
      title: 'Utrustning',
      items: [
        { name: 'Pakethållare', tip: '' },
        { name: 'Skärmar', tip: '' },
        { name: 'Stöd', tip: '' },
        { name: 'Cykelkorg', tip: '' },
        {
          name: 'Lås',
          tip:
            'Vissa cyklar kommer utrustade med ramlås. Ett tips är att köpa till ett vajerlås för att säkra din cykel ytterligare. '
        },
        {
          name: 'SSF-Godkänt lås',
          tip:
            'Ett SSF-godkänt lås är godkänt av Stöldskyddsföreingen efter att ha genomgått tester som slår fast att låset uppfyller kraven. Tänk på att det ofta krävs ett godkänt cykellås för att försäkringen ska gälla. '
        },
        { name: 'Belysning', tip: '' },
        { name: 'Ringklocka', tip: '' },
        { name: 'Övrigt', tip: '' },
        {
          name: 'Navdynamo',
          tip:
            'Navdynamo är en generator som sitter i navet på framhjulet. När du börjar trampa ger generatorn ström till framlyset, vissa kan också ge till baklyset. Den är väldigt underhållsfri och det enda du behöver ha koll på är om lyset fungerar när hjulet snurrar. Observera att när du leder cykeln blir lyset svagare då hjulet snurrar saktare. '
        }
      ]
    }
  ];

  const productSpecifications = groups.map(group => {
    return {
      title: group.title,
      items: [
        group.items.map(item => {
          const tip = item?.tip;
          const returnItem = product?.customFields.find(
            customField => customField?.title === item?.name
          );
          if (returnItem && tip) {
            return { ...returnItem, tip };
          }
          return returnItem;
        })
      ]
    };
  });

  return (
    <ProductContainer>
      <InnerWrapper>
        <ImageColumn>
          {images && (
            <>
              <div className="fav-wrapper">
                <Favourite product={product} variant={selectedVariation} />
              </div>
              <ImageContainer
                images={images}
                variant={selectedVariation}
                badges={product.badges}
              />
            </>
          )}
        </ImageColumn>

        <ProductInfoColumn>
          <div className="inner">
            <Breadcrumbs
              breadcrumbs={data?.route?.breadcrumbs || []}
              parents={parents}
            />

            <div className="product-titles-wrapper">
              <div className="product-titles">
                {product?.subName && (
                  <h4 className="sub-name">{product.subName || '\u00A0'}</h4>
                )}
                <ProductTitle data-testid="product-title">
                  {product.name || 'Loading...'}
                </ProductTitle>
              </div>
              <ShortDescription>
                <p>{product?.shortDescription}</p>
              </ShortDescription>
            </div>

            {product?.isPackage ? (
              <LoadablePackageProduct product={product} />
            ) : (
              <AddToCartForm
                product={product}
                variant={selectedVariation}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            )}

            <UspsWrapper>
              <DynamicCategoryRenderer
                categoryId={pdpUspId}
                rendererComponents={uspComponents}
              />
            </UspsWrapper>
          </div>
        </ProductInfoColumn>
      </InnerWrapper>
      <BottomWrapper>
        {product?.description && (
          <ProductInfo
            product={product}
            productSpecifications={productSpecifications}
          />
        )}
        {relatedProducts?.length > 0 && (
          <>
            <h2>Du kanske också är intresserad av</h2>
            <ProductRow>
              {relatedProducts.map(product => {
                return <ProductRowItem product={product} />;
              })}
            </ProductRow>
          </>
        )}
      </BottomWrapper>
    </ProductContainer>
  );
};

export default Product;
